import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GrowthShell from '../../components/growth/GrowthShell';
import RangeBar from '../../components/growth/GrowthRange';
import ClaritySessions from '../../components/growth/ClaritySessions';
import { fetchFeatureUseBetween, showUnused } from '../../lib/growthFeatures';
import { CLARITY_FIRST_DAY, clarityUrl, coveredDay, fetchClarityRange } from '../../lib/growthClarity';
import { zeroResultSearches } from '../../lib/claritySessions';
import { fetchFindings } from '../../lib/growthFindings';
import { CHANNEL_AR, CLARITY_METRICS, DEVICE_AR, OS_AR, eventAr, sourceName } from '../../lib/growthLabels';
import { MIN_SAMPLE, addDays, fmtAt, fmtDay, rangeLabel, rangeWindow, readRange, riyadhDay, spanLabel, writeRange } from '../../lib/growthRange';

// Growth OS «تحليل السلوك» — owner rework, 2026-11-01: read at a glance.
// Everything follows the shared range (URL): the findings, Clarity (one stored
// pull per day, the days inside the range), searches without results and the
// features list. Arabic names everywhere; the technical name is a tooltip.

const LOW = 'بيانات قليلة';
const fmt = (n, d = 0) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d });
const PULL_AR = { ok: 'نجح', partial: 'جزئي', error: 'فشل', skipped: 'تُخطّي', running: 'يعمل' };

function Low({ base }) {
  return <span className="gos-low" data-low-sample="1" title={`أقل من ${MIN_SAMPLE}: لا يُبنى عليه قرار بعد`}><span className="gos-pill low">{LOW} ({fmt(base)})</span></span>;
}

function Delta({ now, before, low }) {
  if (before == null) return null;
  if (!before) return now ? <span className="gos-faint">جديد</span> : null;
  const pct = Math.round(((now - before) / before) * 100);
  if (low) return <span className="gos-pill low" data-low-sample="1" title={`${LOW} (الحد ${MIN_SAMPLE})`}>{pct > 0 ? '▲' : pct < 0 ? '▼' : ''} {Math.abs(pct)}%</span>;
  if (pct === 0) return <span className="gos-pill neutral">0%</span>;
  return <span className={`gos-pill ${pct > 0 ? 'good' : 'bad'}`}>{pct > 0 ? '▲' : '▼'} {Math.abs(pct)}%</span>;
}

// ── «أهم الملاحظات» ───────────────────────────────────────────────────────────
function Findings({ win }) {
  const [f, setF] = useState(undefined);
  useEffect(() => {
    let alive = true; setF(undefined);
    fetchFindings(win).then((r) => { if (alive) setF(r); }).catch((e) => { console.warn('[GrowthBehavior] findings:', e?.message || e); if (alive) setF(null); });
    return () => { alive = false; };
  }, [win.start, win.end]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="gos-card gos-findings" data-section="findings" style={{ marginBottom: 16 }}>
      <div className="gos-card-head"><h3>أهم الملاحظات</h3>{f && <span className="gos-pcount">{fmt(f.sessions)} زيارة في الفترة</span>}</div>
      {f === undefined ? <div className="gos-empty-state">…</div> : f === null ? <div className="gos-empty-state">تعذّر التحميل.</div>
        : f.list.length === 0 ? <div className="gos-empty-state"><strong>لا ملاحظات في هذه الفترة</strong>لم يحدث ما يستحق التنبيه بعد.</div> : (
          <ol className="gos-find-list">
            {f.list.map((x) => (
              <li key={x.key} data-finding={x.key} className={x.low ? 'low' : ''}>
                <p>{x.text}. {x.low && <Low base={x.base} />}</p>
                <ClaritySessions sessions={x.sessions} label="شاهد الجلسات" />
              </li>
            ))}
          </ol>
        )}
      {f?.capped && <p className="gos-note">الفترة فيها أحداث أكثر مما يُحمَّل مرة واحدة؛ الملاحظات من أول {fmt(20000)} حدث.</p>}
    </section>
  );
}

// ── Clarity ──────────────────────────────────────────────────────────────────
function MetricCard({ m, v }) {
  let value; let sub; let low = false;
  if (!v || !v.seen) { value = '—'; sub = 'لا بيانات في هذه الأيام'; }
  else if (m.kind === 'count') {
    value = v.pct == null ? '—' : `${fmt(v.pct, 1)}%`;
    sub = `من الجلسات · ${fmt(v.total)} ${m.key === 'ScriptErrorCount' ? 'خطأ' : 'مرة'} في ${fmt(v.withIt)} من ${fmt(v.sessions)} جلسة`;
    low = v.sessions < MIN_SAMPLE;
  } else if (m.kind === 'scroll') {
    value = v.scroll == null ? '—' : `${fmt(v.scroll, 0)}%`; sub = 'من طول الصفحة في المتوسط';
  } else {
    value = v.active == null ? '—' : `${fmt(v.active, 0)} ث`; sub = v.totalTime == null ? '' : `نشطة من ${fmt(v.totalTime, 0)} ث للجلسة في المتوسط`;
  }
  return (
    <div className={`gos-mcard${m.bad && v?.pct > 0 ? ' warn' : ''}`} data-metric={m.key} title={m.key}>
      <p className="gos-mcard-name">{m.ar}</p>
      <p className={`gos-mcard-value${low ? ' low' : ''}`}><bdi dir="ltr">{value}</bdi></p>
      <p className="gos-mcard-sub">{sub} {low && <Low base={v.sessions} />}</p>
      <p className="gos-mcard-note">{m.note}</p>
    </div>
  );
}

const cell = (v, kind) => {
  if (!v || !v.seen) return <span className="gos-faint">—</span>;
  if (kind === 'scroll') return v.scroll == null ? '—' : `${fmt(v.scroll)}%`;
  if (kind === 'time') return v.active == null ? '—' : `${fmt(v.active)} / ${fmt(v.totalTime)} ث`;
  return v.total ? <span title={`${fmt(v.withIt)} من ${fmt(v.sessions)} جلسة`}>{fmt(v.total)} <small className="gos-faint">({fmt(v.pct, 0)}%)</small></span> : <span className="gos-faint">0</span>;
};

function TrafficTable({ title, rows, name }) {
  if (!rows?.length) return null;
  return (
    <div>
      <h4 className="gos-subhead">{title}</h4>
      <table className="gos-table">
        <thead><tr><th>البند</th><th>الجلسات</th><th>صفحات للجلسة</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.key}><td title={r.key}>{name(r)}</td><td className="num">{fmt(r.sessions)}</td><td className="num">{r.pagesPerSession == null ? '—' : fmt(r.pagesPerSession, 1)}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function ClaritySection({ range, prevRange, compare }) {
  const [c, setC] = useState(undefined);
  const [prev, setPrev] = useState(null);
  useEffect(() => {
    let alive = true; setC(undefined); setPrev(null);
    fetchClarityRange(range.from, range.to).then((r) => { if (alive) setC(r); }).catch((e) => { console.warn('[GrowthBehavior] clarity:', e?.message || e); if (alive) setC(null); });
    if (compare) fetchClarityRange(prevRange.from, prevRange.to).then((r) => { if (alive) setPrev(r.summary); }).catch(() => {});
    return () => { alive = false; };
  }, [range.from, range.to, compare]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = c?.summary;
  const missing = useMemo(() => {
    if (!s) return [];
    const have = new Set(s.days.map((d) => d.day)); const out = [];
    const today = riyadhDay();
    for (let d = range.from; d <= range.to; d = addDays(d, 1)) if (!have.has(d) && d < today && d >= CLARITY_FIRST_DAY) out.push(d);
    return out;
  }, [s, range.from, range.to]);
  return (
    <section className="gos-card" data-section="clarity" style={{ marginBottom: 16 }}>
      <div className="gos-card-head"><h3>ماذا يفعل الزوار في الصفحة (Microsoft Clarity)</h3>
        {clarityUrl() && <a className="gos-link" href={clarityUrl('recordings')} target="_blank" rel="noopener noreferrer">كل التسجيلات ↗</a>}</div>
      <p className="gos-card-sub">يُسحب مرة كل يوم ويُحفظ هنا؛ تُجمع الأيام التي تقع في الفترة.</p>
      {c === undefined ? <div className="gos-empty-state">…</div> : c === null ? <div className="gos-empty-state">تعذّر التحميل.</div> : !s ? (
        <div className="gos-empty-state"><strong>لا توجد بيانات بعد</strong>لا يوجد سحب ناجح لأيام هذه الفترة.</div>
      ) : (
        <>
          <div className="gos-days" data-days={s.days.length}>
            {s.days.map((d) => (
              <span key={d.day} className="gos-day-chip" data-day={d.day}><b>{fmtDay(d.day)}</b> · سُحب {fmtAt(d.pull.started_at)}</span>
            ))}
            {missing.length > 0 && <span className="gos-day-chip missing">لا سحب لـ {missing.length === 1 ? fmtDay(missing[0]) : `${fmt(missing.length)} أيام`}</span>}
            {range.from < CLARITY_FIRST_DAY && <span className="gos-day-chip missing">السحب اليومي بدأ في {fmtDay(CLARITY_FIRST_DAY)}</span>}
          </div>
          <div className="gos-ctotal">
            <span><b>{fmt(s.total.sessions)}</b> جلسة</span>
            {compare && prev && <Delta now={s.total.sessions} before={prev.total.sessions} low={Math.min(s.total.sessions, prev.total.sessions) < MIN_SAMPLE} />}
            {s.total.bots > 0 && <span className="gos-faint">· {fmt(s.total.bots)} جلسة آلية (روبوتات) مستبعدة</span>}
          </div>
          {s.includesTeam && <p className="gos-note low" data-team-note="1">أيام حتى 1 نوفمبر تشمل جلسات الفريق في لوحة الإدارة (قبل إيقاف التسجيل فيها)؛ الأرقام العامة أدناه تتأثر بها، وجدول الصفحات لا.</p>}
          <div className="gos-mcards">
            {CLARITY_METRICS.map((m) => <MetricCard key={m.key} m={m} v={s.metrics[m.key]} />)}
          </div>
          {s.pages.length > 0 && (
            <>
              <h4 className="gos-subhead" style={{ marginTop: 18 }}>حسب الصفحة</h4>
              <div className="gos-table-scroll">
                <table className="gos-table gos-table-wide" data-pages="1">
                  <thead><tr><th>الصفحة</th><th>الجلسات</th>{CLARITY_METRICS.map((m) => <th key={m.key} title={m.note}>{m.ar}</th>)}</tr></thead>
                  <tbody>{s.pages.map((p) => (
                    <tr key={p.path} data-page={p.path}>
                      <td dir="ltr" style={{ textAlign: 'right', wordBreak: 'break-all' }}>{p.path}</td>
                      <td className="num">{fmt(p.sessions)}</td>
                      {CLARITY_METRICS.map((m) => <td key={m.key} className="num">{cell(p.m[m.key], m.kind)}</td>)}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <p className="gos-note">في خانات النقرات والأخطاء: العدد، وبين القوسين نسبة الجلسات التي حدث فيها. صفحات لوحة الإدارة لا تظهر هنا.</p>
            </>
          )}
          <div className="gos-clarity-grid" style={{ marginTop: 16 }}>
            <TrafficTable title="الأجهزة" rows={s.device} name={(r) => [DEVICE_AR[r.parts.Device] || r.parts.Device, OS_AR[r.parts.OS] || r.parts.OS].filter(Boolean).join(' · ')} />
            <TrafficTable title="من أين جاؤوا" rows={s.channels} name={(r) => CHANNEL_AR[r.parts.Channel] || r.parts.Channel} />
            <TrafficTable title="المصادر" rows={s.sources} name={(r) => {
              const src = r.parts.Source ? sourceName(r.parts.Source) : 'بلا مصدر (مباشر)';
              return [src, r.parts.Medium === 'cpc' ? 'إعلان' : r.parts.Medium === 'organic' ? 'بحث مجاني' : r.parts.Medium, r.parts.Campaign].filter(Boolean).join(' · ');
            }} />
          </div>
        </>
      )}
    </section>
  );
}

// ── Searches that found nothing ──────────────────────────────────────────────
function ZeroResults({ win }) {
  const [rows, setRows] = useState(undefined);
  useEffect(() => {
    let alive = true; setRows(undefined);
    zeroResultSearches(win.start, win.end).then((r) => { if (alive) setRows(r); }).catch(() => { if (alive) setRows(null); });
    return () => { alive = false; };
  }, [win.start, win.end]);
  return (
    <section className="gos-card" data-section="zero-results" style={{ marginBottom: 16 }}>
      <div className="gos-card-head"><h3>بحث بلا نتائج</h3>{rows && <span className="gos-pcount">{fmt(rows.length)} عبارة</span>}</div>
      <p className="gos-card-sub">ما بحث عنه الزوار ولم يجدوا شيئاً، مرتّباً بعدد المرات. أرقام الجوال تُحذف من نص البحث قبل حفظه.</p>
      {rows === undefined ? <div className="gos-empty-state">…</div> : rows === null ? <div className="gos-empty-state">تعذّر التحميل.</div>
        : rows.length === 0 ? <div className="gos-empty-state"><strong>لا توجد بيانات بعد</strong>لم ينتهِ أي بحث بلا نتائج في هذه الفترة.</div> : (
          <div className="gos-zr">
            {rows.map((r) => (
              <div key={r.query} className="gos-zr-row" data-query={r.query}>
                <div className="gos-zr-head"><b dir="auto">{r.query}</b><span className="gos-faint">{fmt(r.n)} مرة</span></div>
                <ClaritySessions sessions={r.sessions} />
              </div>
            ))}
          </div>
        )}
    </section>
  );
}

// ── Features ─────────────────────────────────────────────────────────────────
function EventTable({ list, prevBy, compare }) {
  return (
    <div className="gos-table-scroll">
      <table className="gos-table">
        <thead><tr><th>ماذا فعل الزائر</th><th className="gos-hide-sm">القسم</th><th>مرات</th><th className="gos-hide-sm">زيارات</th>{compare && <th className="gos-hide-sm">مقارنة</th>}<th>آخر مرة</th></tr></thead>
        <tbody>{list.map((r) => {
          const before = prevBy ? (prevBy[r.event_name]?.n_recent ?? 0) : null;
          return (
            <tr key={r.event_name} data-event={r.event_name}>
              <td title={r.event_name}>{eventAr(r.event_name)}</td>
              <td className="gos-hide-sm">{r.area}</td>
              <td className="num">{fmt(r.n_recent)}</td>
              <td className="num gos-hide-sm">{r.sessions == null ? '—' : fmt(r.sessions)}</td>
              {compare && <td className="num gos-hide-sm"><Delta now={r.n_recent} before={before} low={Math.min(r.n_recent, before || 0) < MIN_SAMPLE} /></td>}
              <td className="gos-when">{r.last_seen ? fmtAt(r.last_seen) : r.last_seen_all ? <span className="gos-faint" title="آخر مرة قبل هذه الفترة">{fmtAt(r.last_seen_all)}</span> : '—'}</td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function Features({ win, range, compare }) {
  const [rows, setRows] = useState(undefined);
  const [prev, setPrev] = useState(null);
  useEffect(() => {
    let alive = true; setRows(undefined); setPrev(null);
    fetchFeatureUseBetween(win.start, win.end).then((r) => { if (alive) setRows(r); })
      .catch((e) => { console.warn('[GrowthBehavior] features:', e?.message || e); if (alive) setRows(null); });
    if (compare) fetchFeatureUseBetween(win.prevStart, win.start).then((r) => { if (alive) setPrev(r); }).catch(() => {});
    return () => { alive = false; };
  }, [win.start, win.end, compare]); // eslint-disable-line react-hooks/exhaustive-deps
  const prevBy = useMemo(() => (prev ? Object.fromEntries(prev.map((r) => [r.event_name, r])) : null), [prev]);
  if (rows === undefined) return <div className="gos-card"><div className="gos-empty-state">…</div></div>;
  if (rows === null) return <div className="gos-card"><div className="gos-empty-state"><strong>تعذّر تحميل البيانات</strong>أعد تحميل الصفحة.</div></div>;
  // Plumbing, not something a visitor did: shown only under «تفاصيل تقنية».
  const TECH = ['clarity_session_linked'];
  const by = (st) => rows.filter((r) => r.state === st && !TECH.includes(r.event_name)).sort((a, b) => b.n_recent - a.n_recent || eventAr(a.event_name).localeCompare(eventAr(b.event_name), 'ar'));
  const used = by('used'); const unused = by('unused');
  return (
    <>
      <section className="gos-card" data-state="used" style={{ marginBottom: 16 }}>
        <div className="gos-card-head"><h3>ما استخدمه الزوار</h3><span className="gos-pcount">{fmt(used.length)} نوع</span></div>
        <p className="gos-card-sub">كل ما فعله الزوار في الفترة، مرتّباً بعدد المرات. مرّر المؤشر على الاسم لترى اسمه التقني.</p>
        {used.length === 0 ? <div className="gos-empty-state">لا شيء في هذه الفترة.</div> : <EventTable list={used} prevBy={prevBy} compare={compare} />}
      </section>
      {showUnused(range, riyadhDay()) && (
        <section className="gos-card" data-state="unused" style={{ marginBottom: 16 }}>
          <div className="gos-card-head"><h3>لم يُستخدم في هذه الفترة</h3><span className="gos-pcount">{fmt(unused.length)} نوع</span></div>
          <p className="gos-card-sub">الموقع يرسل هذه الأحداث، ولم يحدث أيٌّ منها في الفترة: ميزات يتجاهلها الزوار.</p>
          {unused.length === 0 ? <div className="gos-empty-state">لا شيء هنا.</div> : <EventTable list={unused} prevBy={null} compare={false} />}
        </section>
      )}
      <details className="gos-card gos-tech" data-section="tech">
        <summary>تفاصيل تقنية</summary>
        {[['server', 'من الخادم', 'يسجّلها الخادم نفسه عند حدوثها (مثل تأكيد دفع الرسوم).'],
          ['not_wired', 'غير مربوط بعد', 'لا يرسلها الموقع حالياً: خاصة بالتطبيق، أو ميزات لم تُبنَ. ليست «غير مستخدمة».']].map(([st, title, sub]) => (
          <div key={st} data-state={st} style={{ marginTop: 14 }}>
            <h4 className="gos-subhead">{title} <span className="gos-pcount">{fmt(by(st).length)}</span></h4>
            <p className="gos-card-sub">{sub}</p>
            {by(st).length ? <EventTable list={by(st)} prevBy={null} compare={false} /> : <div className="gos-empty-state">لا شيء هنا.</div>}
          </div>
        ))}
        <div data-state="plumbing" style={{ marginTop: 14 }}>
          <h4 className="gos-subhead">ربط التسجيلات</h4>
          <p className="gos-card-sub">يُسجَّل عند كل جلسة يبدأ فيها Clarity، ليفتح رابط «تشغيل التسجيل» التسجيل نفسه.</p>
          <EventTable list={rows.filter((r) => TECH.includes(r.event_name))} prevBy={null} compare={false} />
        </div>
        <ClarityPulls />
      </details>
    </>
  );
}

function ClarityPulls() {
  const [pulls, setPulls] = useState(null);
  useEffect(() => { fetchClarityRange(addDays(riyadhDay(), -14), riyadhDay()).then((r) => setPulls(r.pulls)).catch(() => setPulls([])); }, []);
  if (!pulls?.length) return null;
  return (
    <div style={{ marginTop: 14 }} data-pulls="1">
      <h4 className="gos-subhead">سجل سحب Clarity (آخر 14 يوماً)</h4>
      <div className="gos-table-scroll">
        <table className="gos-table">
          <thead><tr><th>اليوم</th><th>وقت السحب</th><th>الحالة</th><th>طلبات</th><th>صفوف</th><th>الخطأ</th></tr></thead>
          <tbody>{pulls.map((p) => (
            <tr key={p.id}><td>{fmtDay(coveredDay(p))}</td><td>{fmtAt(p.started_at)}</td><td>{PULL_AR[p.status] || p.status}</td><td className="num">{p.calls}</td><td className="num">{fmt(p.rows_saved)}</td>
              <td dir="ltr" style={{ textAlign: 'right', fontSize: 11, color: 'var(--gos-faint)', wordBreak: 'break-all' }}>{p.error || '—'}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function GrowthBehavior({ user, profile, lang }) {
  const [params, setParams] = useSearchParams();
  const range = useMemo(() => readRange(params), [params]);
  const win = useMemo(() => rangeWindow(range), [range]);
  const prevRange = useMemo(() => ({ from: addDays(range.from, -win.days), to: addDays(range.from, -1) }), [range.from, win.days]);
  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="behavior" title="تحليل السلوك"
                 actions={<span className="gos-top-range">{spanLabel(range.from, range.to)}</span>}>
      <div className="gos-hello">
        <h2>كيف يتصرف الزوار</h2>
        <p>{rangeLabel(range)}. ملاحظات مكتوبة من البيانات، ثم ما سجّله Clarity، ثم كل ما فعله الزوار في الموقع.</p>
      </div>
      <RangeBar range={range} onChange={(next) => setParams(writeRange(next))} />
      <Findings win={win} />
      <ClaritySection range={range} prevRange={prevRange} compare={range.compare} />
      <ZeroResults win={win} />
      <Features win={win} range={range} compare={range.compare} />
    </GrowthShell>
  );
}
